import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  BadgeCheck,
  ChevronRight,
  CircleDollarSign,
  RefreshCw,
  Timer,
  Users,
} from "lucide-react";
import myAxios from "../AxiosUrl";

const PERIODS = [
  ["7d", "7 days"],
  ["30d", "30 days"],
  ["90d", "90 days"],
  ["1y", "1 year"],
];

const SEGMENTS = {
  registered: {
    label: "Registered users",
    icon: Users,
    color: "#6d5dfc",
    activity: "registrations",
    activityLabel: "joined in this period",
  },
  verified: {
    label: "Email verified",
    icon: BadgeCheck,
    color: "#0ea5a4",
    activity: "verifiedRegistrations",
    activityLabel: "verified accounts joined in this period",
  },
  trial: {
    label: "TopSekk free month",
    icon: Timer,
    color: "#f59e0b",
    activity: "trialStarts",
    activityLabel: "free months started in this period",
  },
  paid: {
    label: "Paid subscription",
    icon: CircleDollarSign,
    color: "#16a34a",
    activity: "paidStarts",
    activityLabel: "paid billing periods started",
  },
};

const PLATFORM_META = {
  app_store: { label: "Apple App Store", color: "#111827" },
  google_play: { label: "Google Play", color: "#34a853" },
  stripe: { label: "Stripe", color: "#635bff" },
};

const number = (value) => Number(value || 0).toLocaleString();
const UserOverviewSection = ({ compact = false, selectedSegment = null, onSegmentChange }) => {
  const [period, setPeriod] = useState("30d");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    setData(null);
    try {
      const response = await myAxios.get("/dashboard/user-analytics", {
        params: { period, segment: selectedSegment || "registered", page: 1, limit: 5 },
      });
      setData(response.data.data);
    } catch (requestError) {
      setError(
        requestError.response?.data?.message || "User analytics could not be loaded."
      );
    } finally {
      setLoading(false);
    }
  }, [period, selectedSegment]);

  useEffect(() => {
    load();
  }, [load]);

  const platformData = useMemo(
    () =>
      (data?.platforms || [])
        .map((item) => ({
          ...item,
          value: item.trial + item.paid,
          ...PLATFORM_META[item.platform],
        }))
        .filter((item) => item.value > 0),
    [data]
  );

  const changeSegment = (value) => {
    onSegmentChange?.(value);
  };

  return (
    <section
      className={`user-overview ${compact ? "user-overview--compact" : ""}`}
      aria-label={compact ? "User overview" : undefined}
      aria-labelledby={compact ? undefined : "user-overview-title"}
    >
      {!compact && <div className="user-overview__header">
        <div>
          <span className="user-overview__eyebrow">USER OVERVIEW</span>
          <h2 id="user-overview-title">Registration & subscription health</h2>
          <p>Live account and subscription records in one place.</p>
        </div>
        <div className="user-overview__actions">
          <div className="period-picker" aria-label="Analytics period">
            {PERIODS.map(([value, label]) => (
              <button
                type="button"
                key={value}
                className={period === value ? "active" : ""}
                aria-pressed={period === value}
                onClick={() => {
                  setPeriod(value);
                }}
              >
                {label}
              </button>
            ))}
          </div>
          <button
            type="button"
            className="overview-refresh"
            onClick={load}
            disabled={loading}
            aria-label="Refresh user analytics"
          >
            <RefreshCw size={17} className={loading ? "spin" : ""} />
          </button>
        </div>
      </div>}

      {error && (
        <div className="user-overview__error" role="alert">
          <span>{error}</span>
          <button type="button" onClick={load}>Try again</button>
        </div>
      )}

      <div className="overview-kpis" aria-busy={loading}>
        {Object.entries(SEGMENTS).map(([key, config]) => {
          const Icon = config.icon;
          return (
            <button
              type="button"
              key={key}
              className={`overview-kpi ${selectedSegment === key ? "selected" : ""}`}
              onClick={() => changeSegment(key)}
              style={{ "--accent": config.color }}
              aria-pressed={selectedSegment === key}
            >
              <span className="overview-kpi__icon"><Icon size={21} /></span>
              <span className="overview-kpi__copy">
                <span className="overview-kpi__label">{config.label}</span>
                <strong>{loading && !data ? "—" : number(data?.snapshot?.[key])}</strong>
                <small>
                  +{number(data?.periodActivity?.[config.activity])} {config.activityLabel}
                </small>
              </span>
              <ChevronRight className="overview-kpi__arrow" size={18} />
            </button>
          );
        })}
      </div>

      {!compact && <>
      <div className="overview-charts">
        <div className="overview-panel overview-panel--trend">
          <div className="overview-panel__heading">
            <div>
              <h3>Growth over time</h3>
              <p>Registrations and the currently verified share of each signup cohort.</p>
            </div>
            <div className="overview-legend">
              <span><i className="purple" />Registered</span>
              <span><i className="teal" />Currently verified</span>
            </div>
          </div>
          <div className="overview-chart">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data?.trend || []} margin={{ top: 10, right: 8, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="registrationsFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#6d5dfc" stopOpacity={0.3} />
                    <stop offset="100%" stopColor="#6d5dfc" stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="#edf0f5" strokeDasharray="4 4" vertical={false} />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#7b8190" }} axisLine={false} tickLine={false} minTickGap={28} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#7b8190" }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={{ border: 0, borderRadius: 12, boxShadow: "0 10px 30px rgba(20,25,40,.12)" }} />
                <Area type="monotone" dataKey="registrations" name="Registered" stroke="#6d5dfc" strokeWidth={3} fill="url(#registrationsFill)" />
                <Area type="monotone" dataKey="verifiedRegistrations" name="Currently verified" stroke="#0ea5a4" strokeWidth={2} fill="transparent" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="overview-panel overview-panel--stores">
          <div className="overview-panel__heading">
            <div>
              <h3>Subscription stores</h3>
              <p>Current trials and paid subscriptions.</p>
            </div>
          </div>
          {platformData.length ? (
            <>
              <div className="store-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={platformData} dataKey="value" nameKey="label" innerRadius={54} outerRadius={78} paddingAngle={4}>
                      {platformData.map((item) => <Cell key={item.platform} fill={item.color} />)}
                    </Pie>
                    <Tooltip formatter={(value) => [number(value), "Subscriptions"]} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="store-chart__total">
                  <strong>{number(platformData.reduce((sum, item) => sum + item.value, 0))}</strong>
                  <span>current</span>
                </div>
              </div>
              <div className="store-list">
                {platformData.map((item) => (
                  <div className="store-row" key={item.platform}>
                    <span className="store-dot" style={{ background: item.color }} />
                    <span className="store-name">{item.label}</span>
                    <span><b>{number(item.trial)}</b> trial</span>
                    <span><b>{number(item.paid)}</b> paid</span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="store-empty">No current store subscriptions.</div>
          )}
        </div>
      </div>
      <p className="overview-note">Subscription period figures use recorded trial/current-period dates and current status. Email verification history is unavailable, so “currently verified” refers to the present status of users who registered in the selected period.</p>
      </>}

      <style>{`
        .user-overview--compact{padding:0!important;border:0!important;background:transparent!important;box-shadow:none!important}.user-overview--compact .overview-kpis{margin-top:0}
        .user-overview{margin:24px 0 30px;padding:24px;border:1px solid #e7eaf0;border-radius:24px;background:linear-gradient(145deg,#fff 0%,#fbfbff 100%);box-shadow:0 18px 50px rgba(33,37,59,.07);color:#202433}.user-overview *{box-sizing:border-box}.user-overview__header,.user-overview__actions,.overview-panel__heading,.overview-users__top,.overview-pagination{display:flex;align-items:center;justify-content:space-between;gap:18px}.user-overview__eyebrow{display:block;margin-bottom:6px;color:#6d5dfc;font-size:11px;font-weight:800;letter-spacing:.14em}.user-overview h2{margin:0;font-size:24px;letter-spacing:-.03em}.user-overview h3{margin:0;font-size:17px}.user-overview p{margin:6px 0 0;color:#747b8c;font-size:13px}.period-picker{display:flex;padding:4px;border-radius:12px;background:#f0f1f6}.period-picker button,.overview-refresh,.overview-pagination button{border:0;background:transparent;cursor:pointer}.period-picker button{padding:8px 11px;border-radius:9px;color:#697083;font-size:12px;font-weight:700}.period-picker button.active{background:#fff;color:#4034d8;box-shadow:0 3px 10px rgba(46,49,76,.1)}.overview-refresh{display:grid;width:40px;height:40px;place-items:center;border:1px solid #e4e6ed;border-radius:11px;background:#fff;color:#575e70}.overview-refresh:disabled{cursor:wait;opacity:.6}.spin{animation:overview-spin .8s linear infinite}@keyframes overview-spin{to{transform:rotate(360deg)}}.user-overview__error{display:flex;justify-content:space-between;gap:12px;margin-top:18px;padding:12px 14px;border:1px solid #fecaca;border-radius:12px;background:#fff4f4;color:#b42318;font-size:13px}.user-overview__error button{border:0;background:transparent;color:inherit;font-weight:800;cursor:pointer}.overview-kpis{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:14px;margin-top:22px}.overview-kpi{position:relative;display:flex;min-width:0;align-items:center;gap:12px;padding:17px 14px;border:1px solid #e8eaf0;border-radius:16px;background:#fff;text-align:left;cursor:pointer;transition:.18s ease}.overview-kpi:hover,.overview-kpi.selected{transform:translateY(-2px);border-color:var(--accent);box-shadow:0 10px 25px color-mix(in srgb,var(--accent) 13%,transparent)}.overview-kpi__icon{display:grid;flex:0 0 42px;height:42px;place-items:center;border-radius:13px;background:color-mix(in srgb,var(--accent) 11%,white);color:var(--accent)}.overview-kpi__copy{display:flex;min-width:0;flex:1;flex-direction:column}.overview-kpi__label{overflow:hidden;color:#676e80;font-size:12px;font-weight:700;text-overflow:ellipsis;white-space:nowrap}.overview-kpi strong{margin:2px 0;color:#202433;font-size:25px;line-height:1}.overview-kpi small{overflow:hidden;color:#8b91a0;font-size:10px;text-overflow:ellipsis;white-space:nowrap}.overview-kpi__arrow{flex:0 0 auto;color:#c2c6d0}.overview-charts{display:grid;grid-template-columns:minmax(0,1.7fr) minmax(290px,.8fr);gap:16px;margin-top:16px}.overview-panel{border:1px solid #e8eaf0;border-radius:18px;background:#fff}.overview-panel--trend,.overview-panel--stores{padding:19px}.overview-panel__heading{align-items:flex-start}.overview-panel__heading p{max-width:520px}.overview-legend{display:flex;gap:12px;color:#777e8e;font-size:11px;white-space:nowrap}.overview-legend span{display:flex;align-items:center;gap:5px}.overview-legend i,.store-dot{width:8px;height:8px;border-radius:99px}.overview-legend .purple{background:#6d5dfc}.overview-legend .teal{background:#0ea5a4}.overview-chart{height:250px;margin-top:16px}.store-chart{position:relative;height:190px}.store-chart__total{position:absolute;top:50%;left:50%;display:flex;transform:translate(-50%,-50%);flex-direction:column;align-items:center;pointer-events:none}.store-chart__total strong{font-size:24px}.store-chart__total span{color:#8a90a0;font-size:10px}.store-list{display:flex;flex-direction:column;gap:9px}.store-row{display:grid;grid-template-columns:10px 1fr auto auto;align-items:center;gap:8px;color:#787f8f;font-size:11px}.store-row b{color:#303545}.store-empty{display:grid;height:245px;place-items:center;color:#9398a6;font-size:13px}.overview-users{margin-top:16px;overflow:hidden}.overview-users__top{padding:18px 19px}.overview-search{display:flex;min-width:330px;align-items:center;gap:8px;padding:5px 5px 5px 11px;border:1px solid #dfe2e9;border-radius:11px;color:#9197a5}.overview-search:focus-within{border-color:#6d5dfc;box-shadow:0 0 0 3px rgba(109,93,252,.1)}.overview-search input{min-width:0;flex:1;border:0;outline:0;font:inherit}.overview-search button{padding:7px 11px;border:0;border-radius:8px;background:#202433;color:#fff;font-size:11px;font-weight:700;cursor:pointer}.overview-table-wrap{overflow-x:auto;border-top:1px solid #eceef3;border-bottom:1px solid #eceef3}.overview-table{width:100%;border-collapse:collapse;font-size:12px}.overview-table th{padding:11px 16px;background:#f8f9fb;color:#787f8f;text-align:left;font-size:10px;letter-spacing:.04em;text-transform:uppercase}.overview-table td{padding:13px 16px;border-top:1px solid #f0f1f4;color:#555c6d;white-space:nowrap}.overview-user-cell{display:flex;align-items:center;gap:10px}.overview-user-cell>span{display:grid;width:34px;height:34px;place-items:center;border-radius:10px;background:#efedff;color:#5a4be7;font-weight:800}.overview-user-cell div{display:flex;max-width:230px;flex-direction:column}.overview-user-cell strong{overflow:hidden;color:#292e3c;text-overflow:ellipsis}.overview-user-cell small{overflow:hidden;color:#8a90a0;text-overflow:ellipsis}.status-pill{display:inline-flex;padding:4px 8px;border-radius:99px;background:#f0f1f4;color:#6d7380;font-size:10px;font-weight:800;text-transform:capitalize}.status-pill.verified,.status-pill.active{background:#e8f8ee;color:#15803d}.status-pill.pending{background:#fff3dc;color:#b56806}.status-pill.trialing{background:#fff3dc;color:#b56806}.muted{color:#a0a5b1}.overview-table__empty{height:90px;text-align:center!important;color:#9298a5!important}.overview-pagination{padding:13px 18px;color:#858b99;font-size:11px}.overview-pagination div{display:flex;gap:7px}.overview-pagination button{display:grid;width:32px;height:30px;place-items:center;border:1px solid #e0e3e9;border-radius:8px;background:#fff;color:#555c6c}.overview-pagination button:disabled{cursor:not-allowed;opacity:.4}.overview-note{margin:12px 3px 0!important;font-size:10px!important}.sr-only{position:absolute;width:1px;height:1px;padding:0;margin:-1px;overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap;border:0}@media(max-width:1000px){.overview-kpis{grid-template-columns:repeat(2,1fr)}.overview-charts{grid-template-columns:1fr}}@media(max-width:680px){.user-overview{padding:17px;border-radius:18px}.user-overview__header,.user-overview__actions,.overview-users__top{align-items:stretch;flex-direction:column}.user-overview__actions{gap:9px}.period-picker{display:grid;grid-template-columns:repeat(4,1fr)}.overview-refresh{width:100%}.overview-kpis{grid-template-columns:1fr}.overview-legend{display:none}.overview-search{min-width:0;width:100%}.overview-chart{height:220px}}
      `}</style>
    </section>
  );
};

export default UserOverviewSection;
